import { defineComponent, PropType } from "vue";
import { useVuelidate } from "@vuelidate/core";
import { Container } from "aurelia-dependency-injection";
import { required } from "@vuelidate/validators";
import moment from "moment";

import { IPatientSupply } from "../../../../../../resources-vue/vue-interfaces/i-get-supply";
import { formatDate, formatDateInput } from "../../../../../../common/vue-helpers/modifiers/value-modifier";
import { ToastrService } from "../../../../../../services/toastr.service";

import DatePicker from "../../../../../../resources-vue/vue-custom-elements/DatePicker/DatePicker.vue";
import TablePrompt from "../../../../../../resources-vue/vue-custom-elements/TablePrompt/TablePrompt.vue";
import CommentInput from "./../../../../../../resources-vue/vue-custom-elements/CommentInput/CommentInput.vue";

export default defineComponent({
    components: {
        DatePicker,
        TablePrompt,
        CommentInput
    },
    props: {
        supply: {
            type: Object as PropType<IPatientSupply>,
            default: {}
        },
        isEditAllowed: {
            type: Boolean,
            default: false
        },
        isNewSupply: {
            type: Boolean,
            default: false
        },
        updateSupply: {
            type: Function
        },
        cancelNewSupply: {
            type: Function
        },
        deleteSupplyFromList: {
            type: Function
        },
        columns: {
            type: Number,
            default: null
        }
    },
    data() {
        return {
            isEditRequested: false,
            isDeleteRequested: false,
            cacheSupply: null,
            _toastrService: null,
            formatDate,
            formatDateInput,
            v$: useVuelidate()
        };
    },
    computed: {
        isSupplyFormularyBoolean() {
            return typeof this.supply.isFormulary === "boolean";
        },
        isSupplyIsRelatedBoolean() {
            return typeof this.supply.isRelated === "boolean";
        }
    },
    watch: {
        supply: {
            handler() {
                if (this.isNewSupply) {
                    this.isEditRequested = true;
                }
            },
            immediate: true
        }
    },
    validations() {
        return {
            supply: {
                startDate: { required }
            }
        };
    },
    created() {
        this._toastrService = Container.instance.get(ToastrService);
    },
    methods: {
        onDateChange(date: any, name: string) {
            if (name === "supplyStartDate") this.supply.startDate = date;
            if (name === "supplyEndDate") this.supply.endDate = date;
        },
        editSupply() {
            this.isEditRequested = true;
            this.cacheSupply = Object.assign({}, this.supply);
        },
        requestDeleteSupply() {
            this.isDeleteRequested = true;
        },
        async saveEdit() {
            const validated = await this.v$.$validate();
            if (!this.supply.startDate) {
                this._toastrService.error({
                    title: `Start Date Required`,
                    message: `The Start Date is required for a Supply. Please update and try again.`
                });
                return;
            } else if (moment(this.supply.startDate) > moment(this.supply.endDate)) {
                this._toastrService.error({
                    title: `Error`,
                    message: `The End Date cannot be before Start Date. Please update and try again.`
                });
                return;
            }
            if (!validated) {
                return;
            }
            this.updateSupply(this.supply, this.cacheSupply);
            this.isEditRequested = false;
        },
        cancelEdit() {
            if (this.isNewSupply) {
                this.cancelNewSupply();
            } else {
                this.updateSupply(JSON.parse(JSON.stringify(this.cacheSupply)), this.supply);
                this.isEditRequested = false;
            }
        },
        deleteSupply() {
            this.deleteSupplyFromList(this.supply);
            this.isDeleteRequested = false;
        },
        cancelDelete() {
            this.isDeleteRequested = false;
        },
        resultUpdatedAppended(value: string) {
            this.supply.notRelatedComments += value;
        },
        resultUpdated(value: string) {
            this.supply.notRelatedComments = value;
        }
    }
});
